/**
 * LegalShared.tsx
 * Shared layout component used by PrivacyPage, TermsPage, IntellectualPropertyPage.
 */

import type { ReactNode } from "react"

type Section = {
  h: string
  body?: string
  bullets?: string[]
}

type LegalContent = {
  title: string
  updated?: string
  intro?: string
  sections: Section[]
}

export function LegalPage({
  icon,
  content,
}: {
  icon: ReactNode
  content: LegalContent
}) {
  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-gray-100">
      <main className="mx-auto max-w-3xl px-6 py-14">
        {/* Title */}
        <div className="flex items-start gap-3 mb-10">
          <div className="mt-1 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex-shrink-0 text-gray-700 dark:text-gray-300">
            {icon}
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">{content.title}</h1>
            {content.updated && (
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{content.updated}</p>
            )}
          </div>
        </div>

        {/* Intro */}
        {content.intro && (
          <p className="text-sm leading-7 text-gray-600 dark:text-gray-300 mb-10 pb-10 border-b border-gray-100 dark:border-gray-800">
            {content.intro}
          </p>
        )}

        {/* Sections */}
        <div className="space-y-9">
          {content.sections.map((s) => (
            <section key={s.h} className="border-t border-gray-100 dark:border-gray-800 pt-7">
              <h2 className="text-base font-semibold mb-3 text-gray-900 dark:text-gray-100">{s.h}</h2>
              {s.body && (
                <p className="text-sm leading-7 text-gray-600 dark:text-gray-300">{s.body}</p>
              )}
              {s.bullets && (
                <ul className="mt-2 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>
    </div>
  )
}
